/** FE helpers for System Dashboard (thresholds mirrored for display only; server owns staleLevel). */

export const SYSTEM_DASHBOARD_GROUP = 'slotcheck-enterprise-systemDashboard';

export type StaleLevel = 'ok' | 'amber' | 'red' | 'unknown';

export type FreshnessRow = {
  casinoId: number;
  casinoName: string;
  insightInstance?: number | null;
  collection?: string;
  minUpdateDate: string | null;
  maxUpdateDate: string | null;
  currentDateTime: string | null;
  ageMinutes: number | null;
  staleLevel: StaleLevel;
  timeZoneLabel?: string;
  message?: string | null;
};

export function formatAgeMinutes(ageMinutes: number | null | undefined): string {
  if (ageMinutes == null || !Number.isFinite(ageMinutes)) return '—';
  const m = Math.floor(ageMinutes);
  if (m < 60) return `${m}m`;
  const hr = Math.floor(m / 60);
  const rem = m % 60;
  if (hr < 48) return rem ? `${hr}h ${rem}m` : `${hr}h`;
  const days = Math.floor(hr / 24);
  const remHr = hr % 24;
  return remHr ? `${days}d ${remHr}h` : `${days}d`;
}

/** Viewer IANA zone (e.g. America/Los_Angeles). */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
  } catch {
    return 'local';
  }
}

/**
 * Format a true-UTC ISO instant in the viewer's local zone.
 * Age is still computed server-side from UTC (no casino tz adjustment).
 */
export function formatViewerLocal(
  iso: string | null | undefined,
  timeZone: string = viewerTimeZone()
): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  };
  try {
    if (timeZone && timeZone !== 'local') {
      return d.toLocaleString('en-US', { ...opts, timeZone });
    }
  } catch {
    /* fall through */
  }
  return d.toLocaleString('en-US', opts);
}

/** Short zone label for UI (IANA id). */
export function viewerZoneLabel(timeZone: string = viewerTimeZone()): string {
  return timeZone || 'local';
}
